import type { Client } from '@coachapp/shared';
import { Archive, ChevronRight, Search, UserPlus, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import ErrorAlert from '../components/ErrorAlert';
import LeadsPanel from '../components/LeadsPanel';
import { RootHeader } from '../components/RootHeader';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { getApiErrorMessage } from '../lib/api-error';
import { clientListContext, parseClientFilter, type ClientListFilter } from '../lib/client-screen';
import { getClientStatusBadgeClass, isArchivedClient } from '../lib/client-status';
import { useGetClientsQuery } from '../store/coachApi';

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return (parts[0] ?? '').slice(0, 2).toUpperCase();
};

const ClientCard = ({
  client,
  onOpen,
}: {
  client: Client;
  onOpen: () => void;
}) => {
  const { t } = useTranslation();
  const context = clientListContext(client);

  return (
    <article
      role="button"
      tabIndex={0}
      className="card p-4 md:p-5 cursor-pointer group"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="flex items-start gap-3 min-w-0">
        <div
          className="shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-2xl bg-primary-soft text-primary font-bold text-sm flex items-center justify-center"
          aria-hidden
        >
          {getInitials(client.name)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="font-semibold text-slate-900 leading-tight truncate">{client.name}</h2>
              <span className={`inline-block mt-1.5 ${getClientStatusBadgeClass(client.status)}`}>
                {t(`clientStatus.${client.status}`, { defaultValue: client.status })}
              </span>
            </div>
            <ChevronRight
              size={18}
              className="shrink-0 text-slate-300 group-hover:text-primary transition-colors mt-0.5"
              aria-hidden
            />
          </div>

          {context && (
            <p className="mt-3 text-sm text-slate-600 truncate">{context}</p>
          )}
        </div>
      </div>
    </article>
  );
};

const ClientsPage = () => {
  const { t } = useTranslation();
  const navigate = useTransitionNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: clients = [], error, isLoading, refetch } = useGetClientsQuery();
  const [searchQuery, setSearchQuery] = useState('');

  const filter = parseClientFilter(location.search);

  const setFilter = (next: ClientListFilter) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'active') {
      params.delete('filter');
    } else {
      params.set('filter', next);
    }
    setSearchParams(params, { replace: true });
  };

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const matchesSearch = (client: Client) => {
    if (!normalizedQuery) return true;
    const haystack = [client.name, client.email, client.login]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(normalizedQuery);
  };

  const { activeClients, archivedClients } = useMemo(() => {
    const active: Client[] = [];
    const archived: Client[] = [];

    for (const client of clients) {
      if (!matchesSearch(client)) continue;
      if (isArchivedClient(client)) {
        archived.push(client);
      } else {
        active.push(client);
      }
    }

    return { activeClients: active, archivedClients: archived };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, normalizedQuery]);

  const visibleClients = filter === 'archived' ? archivedClients : activeClients;
  const fetchError = error ? getApiErrorMessage(error, 'Не удалось загрузить клиентов') : null;
  const clientsMissing = Boolean(fetchError) && clients.length === 0;

  const chips: Array<{ key: ClientListFilter; label: string; icon: typeof Users }> = [
    { key: 'active', label: t('Активные'), icon: Users },
    { key: 'leads', label: t('Потенциальные клиенты'), icon: UserPlus },
    { key: 'archived', label: t('Архив'), icon: Archive },
  ];

  return (
    <>
      <div className="page-container pb-24">
        <RootHeader title={t('Клиенты')} />

        <div className="@container mb-4">
          <div className="segmented grid-cols-3">
            {chips.map((chip) => {
              const Icon = chip.icon;
              const selected = filter === chip.key;
              return (
                <button
                  key={chip.key}
                  type="button"
                  aria-pressed={selected}
                  aria-label={chip.label}
                  title={chip.label}
                  onClick={() => setFilter(chip.key)}
                  className={`segmented-item ${
                    selected ? 'segmented-item-active' : 'segmented-item-idle'
                  }`}
                >
                  <Icon size={18} className="shrink-0" aria-hidden />
                  <span className="hidden truncate @[28rem]:inline">{chip.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="relative mb-4">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            aria-hidden
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('Поиск по имени или email')}
            className="input-field pl-11"
            aria-label={t('Поиск по имени или email')}
          />
        </div>

        {fetchError && (
          <ErrorAlert
            message={fetchError}
            onRetry={() => void refetch()}
            onDismiss={() => {}}
          />
        )}

        {filter === 'leads' ? (
          <LeadsPanel searchQuery={searchQuery} />
        ) : isLoading && clients.length === 0 ? (
          <div className="flex justify-center py-20">
            <div className="spinner h-8 w-8" />
          </div>
        ) : clientsMissing ? null : visibleClients.length === 0 ? (
          <div className="empty-state p-8 sm:p-12">
            <p className="font-medium text-slate-500">
              {normalizedQuery
                ? t('Клиенты не найдены')
                : filter === 'archived'
                  ? t('Архив пуст')
                  : t('Клиентов пока нет. Нажмите «+», чтобы добавить.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
            {visibleClients.map((client: Client) => (
              <ClientCard
                key={client.id}
                client={client}
                onOpen={() => navigate(`/clients/${client.id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ClientsPage;
